import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdminGetLegalDocuments } from '@/api/auth/admin/admin';
import { AddLegalDocumentVersionDialog } from '@/components/admin/AddLegalDocumentVersionDialog';
import { useNavigate } from 'react-router-dom';
import { LegalDocumentVersionResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { FileText, Scale } from 'lucide-react';

type DocumentType = 'privacy-policy' | 'terms-of-use';

interface DocumentTypeConfig {
  type: DocumentType;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const DOCUMENT_TYPES: DocumentTypeConfig[] = [
  {
    type: 'privacy-policy',
    title: 'Privacy Policy',
    description: 'Describes how user data is collected and used',
    icon: <FileText className="h-5 w-5" />,
  },
  {
    type: 'terms-of-use',
    title: 'Terms of Use',
    description: 'Legal agreement between the service and users',
    icon: <Scale className="h-5 w-5" />,
  },
];

/**
 * Admin Legal Documents page for managing Privacy Policy and Terms of Use
 */
export default function LegalDocumentsPage() {
  const navigate = useNavigate();

  const {
    data: documentsData,
    isLoading,
    error,
  } = useAdminGetLegalDocuments();

  const privacyPolicy = documentsData?.data?.privacyPolicy;
  const termsOfUse = documentsData?.data?.termsOfUse;

  const getVersionsForType = (type: DocumentType) => {
    if (type === 'privacy-policy') return privacyPolicy?.versions || [];
    return termsOfUse?.versions || [];
  };

  const getActiveVersionForType = (type: DocumentType) => {
    if (type === 'privacy-policy') return privacyPolicy?.activeVersion;
    return termsOfUse?.activeVersion;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Legal Documents</h1>
        <p className="text-muted-foreground">
          Manage Privacy Policy and Terms of Use versions
        </p>
      </div>

      {error ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load legal documents: {(error as any)?.message || 'Unknown error'}</p>
        </div>
      ) : isLoading ? (
        <div className="grid gap-6 grid-cols-1">
          {[...Array(2)].map((_, index) => (
            <Skeleton key={index} className="h-[300px] w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 grid-cols-1">
          {DOCUMENT_TYPES.map((docType) => (
            <DocumentTypeCard
              key={docType.type}
              config={docType}
              versions={getVersionsForType(docType.type)}
              activeVersion={getActiveVersionForType(docType.type)}
              onViewVersion={(version) =>
                navigate(`/dashboard/admin/legal-documents/${docType.type}/versions/${version}`)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface DocumentTypeCardProps {
  config: DocumentTypeConfig;
  versions: LegalDocumentVersionResponse[];
  activeVersion?: string;
  onViewVersion: (version: string) => void;
}

function DocumentTypeCard({ config, versions, activeVersion, onViewVersion }: DocumentTypeCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {config.icon}
            <CardTitle>{config.title}</CardTitle>
          </div>
          <AddLegalDocumentVersionDialog documentType={config.type} documentTitle={config.title} />
        </div>
        <CardDescription>{config.description}</CardDescription>
        {activeVersion && (
          <div className="pt-2">
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              Active: v{activeVersion}
            </Badge>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {versions.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">
            No versions yet. Create a version to get started.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((version) => {
                const isActive = version.version === activeVersion;
                return (
                  <TableRow key={version.id}>
                    <TableCell className="font-mono">v{version.version}</TableCell>
                    <TableCell>
                      {isActive ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {version.createdUtc
                        ? new Date(version.createdUtc).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewVersion(version.version!)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
